import React, { useState, useMemo, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Card,
  CardContent,
  Chip,
  Alert,
  InputAdornment,
  IconButton,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import {
  Close as CloseIcon,
  Save as SaveIcon,
  Info as InfoIcon,
  Image as ImageIcon,
  VideoFile as VideoIcon,
  Description as DocumentIcon,
} from "@mui/icons-material";

const CreateTemplateDialog = ({ open, onClose, onSave, initialValues }) => {
  console.log('initialvalues', initialValues);

  const [formData, setFormData] = useState({
    templateName: "",
    category: "",
    language: "",
    headerType: "text",
    headerText: "",
    headerMediaType: "",
    headerMediaFile: null,
    body: "",
    footer: "",
  });

  // Track sample text for variables separately for header, body, footer
  const [headerVariableSamples, setHeaderVariableSamples] = useState({});
  const [bodyVariableSamples, setBodyVariableSamples] = useState({});
  const [footerVariableSamples, setFooterVariableSamples] = useState({});

  const [ctaButtons, setCtaButtons] = useState([]);
  const [copyCodeLabel, setCopyCodeLabel] = useState("");

  // Prefill form when editing
  useEffect(() => {
    if (open) {
      if (initialValues && initialValues.attrs) {
        setFormData({
          templateName: initialValues.attrs.templateName || "",
          category: initialValues.attrs.category || "",
          language: initialValues.attrs.language || "",
          headerType: initialValues.attrs.headerMediaType ? "media" : "text",
          headerText: initialValues.attrs.headerText || "",
          headerMediaType: initialValues.attrs.headerMediaType || "",
          headerMediaFile: null,
          body: initialValues.attrs.body || "",
          footer: initialValues.attrs.footer || "",
        });

        // Parse variableSamples
        let variableSamples = { header: {}, body: {}, footer: {} };
        try {
          variableSamples = initialValues.attrs.variableSamples ? JSON.parse(initialValues.attrs.variableSamples) : { header: {}, body: {}, footer: {} };
        } catch (e) {}
        setHeaderVariableSamples(variableSamples.header || {});
        setBodyVariableSamples(variableSamples.body || {});
        setFooterVariableSamples(variableSamples.footer || {});

        setCtaButtons(initialValues.attrs.ctaButtons || []);
        setCopyCodeLabel(initialValues.attrs.copyCodeLabel || "");
      } else {
        setFormData({
          templateName: "",
          category: "",
          language: "",
          headerType: "text",
          headerText: "",
          headerMediaType: "",
          headerMediaFile: null,
          body: "",
          footer: "",
        });
        setHeaderVariableSamples({});
        setBodyVariableSamples({});
        setFooterVariableSamples({});
        setCtaButtons([]);
        setCopyCodeLabel("");
      }
    }
  }, [open, initialValues]);

  const [errors, setErrors] = useState({});

  // ...existing code...

  // Helper: extract variables from body
  const extractVariables = (body) => {
    // Matches {{name}} or {{number}}
    const regex = /{{\s*([a-zA-Z0-9_]+)\s*}}/g;
    const variables = [];
    let match;
    while ((match = regex.exec(body))) {
      variables.push(match[1]);
    }
    return variables;
  };

  // Helper: check if all variables are numbered or named
  const getVariableType = (variables) => {
    if (variables.length === 0) return null;
    const isNumbered = variables.every((v) => /^\d+$/.test(v));
    const isNamed = variables.every((v) => /^[a-zA-Z_][a-zA-Z0-9_]*$/.test(v));
    if (isNumbered) return "numbered";
    if (isNamed) return "named";
    return "mixed";
  };

  // Memoized variables from body, header, footer
  const bodyVariables = useMemo(() => extractVariables(formData.body), [formData.body]);
  const headerVariables = useMemo(() => extractVariables(formData.headerText), [formData.headerText]);
  const footerVariables = useMemo(() => extractVariables(formData.footer), [formData.footer]);
  const variableType = useMemo(() => getVariableType(bodyVariables), [bodyVariables]);

  const categories = [
    "Marketing",
    "One-time Password",
    "Utility",
  ];

  // CTA button types
  const ctaTypes = [
    { value: "phone", label: "Phone Number" },
    { value: "website", label: "Website" },
  ];

  const LANGUAGES = [
    { name: 'English (United States)', code: 'en_US' },
    { name: 'English (United Kingdom)', code: 'en_GB' },
    { name: 'Spanish (Spain)', code: 'es_ES' },
    { name: 'Spanish (Mexico)', code: 'es_MX' },
    { name: 'French (France)', code: 'fr_FR' },
    { name: 'French (Canada)', code: 'fr_CA' },
    { name: 'Chinese (Simplified)', code: 'zh_CN' },
    { name: 'Chinese (Traditional)', code: 'zh_TW' },
    { name: 'Arabic (Egypt)', code: 'ar_EG' },
    { name: 'Arabic (Saudi Arabia)', code: 'ar_SA' },
    { name: 'German (Germany)', code: 'de_DE' },
    { name: 'German (Austria)', code: 'de_AT' },
    { name: 'Italian (Italy)', code: 'it_IT' },
    { name: 'Italian (Switzerland)', code: 'it_CH' },
    { name: 'Russian (Russia)', code: 'ru_RU' },
    { name: 'Japanese (Japan)', code: 'ja_JP' },
    { name: 'Korean (South Korea)', code: 'ko_KR' },
    { name: 'Hindi (India)', code: 'hi_IN' },
    { name: 'Bengali (India)', code: 'bn_IN' },
    { name: 'Dutch (Netherlands)', code: 'nl_NL' },
    { name: 'Dutch (Belgium)', code: 'nl_BE' },
    { name: 'Polish (Poland)', code: 'pl_PL' },
    { name: 'Swedish (Sweden)', code: 'sv_SE' },
    { name: 'Norwegian (Norway)', code: 'no_NO' },
    { name: 'Thai (Thailand)', code: 'th_TH' },
    { name: 'Vietnamese (Vietnam)', code: 'vi_VN' },
    { name: 'Indonesian (Indonesia)', code: 'id_ID' },
    { name: 'Malay (Malaysia)', code: 'ms_MY' },
    { name: 'Romanian (Romania)', code: 'ro_RO' },
    { name: 'Czech (Czech Republic)', code: 'cs_CZ' },
    { name: 'Hungarian (Hungary)', code: 'hu_HU' },
    { name: 'Hebrew (Israel)', code: 'he_IL' },
    { name: 'Ukrainian (Ukraine)', code: 'uk_UA' },
    { name: 'Finnish (Finland)', code: 'fi_FI' },
    { name: 'Swahili (Kenya)', code: 'sw_KE' },
    { name: 'Tamil (India)', code: 'ta_IN' },
    { name: 'Telugu (India)', code: 'te_IN' },
    { name: 'Zulu (South Africa)', code: 'zu_ZA' },
    { name: 'Xhosa (South Africa)', code: 'xh_ZA' },
    { name: 'Filipino (Philippines)', code: 'tl_PH' },
  ];

  const mediaTypes = [
    { value: "image", label: "Image", icon: <ImageIcon /> },
    { value: "video", label: "Video", icon: <VideoIcon /> },
    { value: "document", label: "Document", icon: <DocumentIcon /> },
  ];

  const handleInputChange = (field, value) => {
    // If headerType is changed to 'media', set headerText to null
    if (field === "headerType" && value === "media") {
      setFormData((prev) => ({
        ...prev,
        headerType: value,
        headerText: "",
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }

    // Reset variable samples for the changed section only
    if (field === "headerText") {
      const vars = extractVariables(value);
      setHeaderVariableSamples((prev) => {
        const updated = {};
        vars.forEach((v) => {
          updated[v] = prev[v] || "";
        });
        return updated;
      });
    }
    if (field === "body") {
      const vars = extractVariables(value);
      setBodyVariableSamples((prev) => {
        const updated = {};
        vars.forEach((v) => {
          updated[v] = prev[v] || "";
        });
        return updated;
      });
    }
    if (field === "footer") {
      const vars = extractVariables(value);
      setFooterVariableSamples((prev) => {
        const updated = {};
        vars.forEach((v) => {
          updated[v] = prev[v] || "";
        });
        return updated;
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.templateName.trim()) {
      newErrors.templateName = "Template name is required";
    }
    if (!formData.category) {
      newErrors.category = "Category is required";
    }
    if (!formData.language) {
      newErrors.language = "Language is required";
    }
    if (formData.headerType === "text" && formData.headerText.length > 60) {
      newErrors.headerText = "Header text cannot exceed 60 characters";
    }

    // Header variable validation (only one allowed)
    if (formData.headerType === "text" && headerVariables.length > 1) {
      newErrors.headerText = "Only one variable is allowed in header.";
    }
    if (formData.headerType === "text" && headerVariables.length === 1) {
      const v = headerVariables[0];
      if (!headerVariableSamples[v] || !headerVariableSamples[v].trim()) {
        newErrors[`variable_header_${v}`] = `Sample text for '{{${v}}}' in header is required.`;
      }
    }

    // Footer variable validation (no variables allowed)
    if (footerVariables.length > 0) {
      newErrors.footer = "Variables are not allowed in footer.";
    }

    if (!formData.body.trim()) {
      newErrors.body = "Body content is required";
    } else {
      // Validate curly braces structure
      const curlyMatches = formData.body.match(/{{[^}]*}}/g) || [];
      const invalidCurly = curlyMatches.some(
        (v) => !/^{{\s*[a-zA-Z0-9_]+\s*}}$/.test(v)
      );
      if (invalidCurly) {
        newErrors.body = "Invalid variable format. Use {{name}} or {{number}}.";
      }
      // Validate variable type
      if (bodyVariables.length > 0 && variableType === "mixed") {
        newErrors.body =
          "Do not mix named and numbered variables. Use only one type.";
      }
      // Require sample text for each variable
      if (bodyVariables.length > 0) {
        bodyVariables.forEach((v) => {
          if (!bodyVariableSamples[v] || !bodyVariableSamples[v].trim()) {
            newErrors[`variable_body_${v}`] = `Sample text for '{{${v}}}' in body is required.`;
          }
        });
      }
    }

    // CTA validation
    if (formData.category === "One-time Password") {
      if (!copyCodeLabel.trim()) {
        newErrors.copyCodeLabel = "Copy Code Label is required.";
      }
    } else {
      ctaButtons.forEach((btn, idx) => {
        if (!btn.label || !btn.label.trim()) {
          newErrors[`cta_label_${idx}`] = "Button label is required.";
        }
        if (!btn.value || !btn.value.trim()) {
          newErrors[`cta_value_${idx}`] = `Button value is required.`;
        }
        // Simple validation for phone/website
        if (btn.type === "phone" && !/^\+?\d{7,}$/.test(btn.value.trim())) {
          newErrors[`cta_value_${idx}`] = "Enter a valid phone number.";
        }
        if (btn.type === "website" && !/^https?:\/\/.+/.test(btn.value.trim())) {
          newErrors[`cta_value_${idx}`] = "Enter a valid website URL (http/https).";
        }
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (validateForm()) {
      // Pass variableSamples and CTA info along with formData
      onSave({
        ...formData,
        variableSamples: {
          header: headerVariableSamples,
          body: bodyVariableSamples,
          footer: footerVariableSamples,
        },
        ctaButtons,
        copyCodeLabel,
      });
      handleClose();
    }
  };

  const handleClose = () => {
    setFormData({
      templateName: "",
      category: "",
      language: "",
      headerType: "text",
      headerText: "",
      headerMediaType: "",
      body: "",
      footer: "",
    });
    setErrors({});
    onClose();
  };

  const renderSection = (title, description, children) => (
    <Card sx={{ mb: 3, border: "1px solid #e0e0e0", borderRadius: 2 }}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, color: "#333", mb: 0.5 }}
          >
            {title}
          </Typography>
          {description && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: "0.875rem" }}
            >
              {description}
            </Typography>
          )}
        </Box>
        {children}
      </CardContent>
    </Card>
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle sx={{ pb: 1, borderBottom: "1px solid #e0e0e0" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 600, color: "#333" }}>
            Create Template Message
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {/* Section 1: Template Info */}
          {renderSection(
            "Template Information",
            null,
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                label="Template Name"
                value={formData.templateName}
                onChange={(e) =>
                  handleInputChange("templateName", e.target.value)
                }
                error={!!errors.templateName}
                helperText={errors.templateName}
                fullWidth
                size="small"
                placeholder="Enter template name"
              />

              <Box sx={{ display: "flex", gap: 2 }}>
                <FormControl fullWidth size="small" error={!!errors.category}>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={formData.category}
                    onChange={(e) =>
                      handleInputChange("category", e.target.value)
                    }
                    label="Category"
                  >
                    {categories.map((category) => (
                      <MenuItem key={category} value={category}>
                        {category}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.category && (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{ mt: 0.5, ml: 1.5 }}
                    >
                      {errors.category}
                    </Typography>
                  )}
                </FormControl>

                <FormControl fullWidth size="small" error={!!errors.language}>
                  <InputLabel>Language</InputLabel>
                  <Select
                    value={formData.language}
                    onChange={(e) =>
                      handleInputChange("language", e.target.value)
                    }
                    label="Language"
                  >
                    {LANGUAGES.map((lang) => (
                      <MenuItem key={lang.code} value={lang.code}>
                        {lang.name} ({lang.code})
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.language && (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{ mt: 0.5, ml: 1.5 }}
                    >
                      {errors.language}
                    </Typography>
                  )}
                </FormControl>
              </Box>
            </Box>
          )}

          {/* Section 2: Header */}
          {renderSection(
            "Header",
            "Add a title or choose a media type for the header. Only one variable is allowed in header.",
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Header Type</InputLabel>
                <Select
                  value={formData.headerType}
                  onChange={(e) =>
                    handleInputChange("headerType", e.target.value)
                  }
                  label="Header Type"
                >
                  <MenuItem value="text">Text</MenuItem>
                  <MenuItem value="media">Media</MenuItem>
                </Select>
              </FormControl>

              {formData.headerType === "text" ? (
                <>
                  <TextField
                    label="Header Text"
                    value={formData.headerText}
                    onChange={(e) =>
                      handleInputChange("headerText", e.target.value)
                    }
                    error={!!errors.headerText}
                    helperText={
                      errors.headerText ||
                      `${formData.headerText.length}/60 characters`
                    }
                    fullWidth
                    size="small"
                    placeholder="Enter header text (max 60 characters)"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Typography variant="caption" color="text.secondary">
                            {formData.headerText.length}/60
                          </Typography>
                        </InputAdornment>
                      ),
                    }}
                  />
                  {/* Header variable sample field */}
                  {headerVariables.length === 1 && (
                    <TextField
                      sx={{ mt: 2 }}
                      label={`Sample for {{${headerVariables[0]}}}`}
                      value={headerVariableSamples[headerVariables[0]] || ""}
                      onChange={(e) =>
                        setHeaderVariableSamples((prev) => ({ ...prev, [headerVariables[0]]: e.target.value }))
                      }
                      error={!!errors[`variable_header_${headerVariables[0]}`]}
                      helperText={errors[`variable_header_${headerVariables[0]}`] || `Enter sample text for '{{${headerVariables[0]}}}'`}
                      fullWidth
                      size="small"
                    />
                  )}
                </>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <FormControl component="fieldset">
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, fontWeight: 500 }}
                    >
                      Select Media Type:
                    </Typography>
                    <RadioGroup
                      value={formData.headerMediaType}
                      onChange={(e) => {
                        handleInputChange("headerMediaType", e.target.value);
                        // Clear file if type changes
                        setFormData((prev) => ({ ...prev, headerMediaFile: null }));
                      }}
                      sx={{ gap: 1 }}
                    >
                      {mediaTypes.map((type) => (
                        <FormControlLabel
                          key={type.value}
                          value={type.value}
                          control={<Radio size="small" />}
                          label={
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              {type.icon}
                              <Typography variant="body2">
                                {type.label}
                              </Typography>
                            </Box>
                          }
                          sx={{
                            margin: 0,
                            padding: 1,
                            borderRadius: 1,
                            border: "1px solid #e0e0e0",
                            "&:hover": {
                              backgroundColor: "#f5f5f5",
                            },
                            "&.Mui-checked": {
                              borderColor: "#1976d2",
                              backgroundColor: "#e3f2fd",
                            },
                          }}
                        />
                      ))}
                    </RadioGroup>
                  </FormControl>

                  {/* File upload for media */}
                  <Box>
                    <Button
                      variant="outlined"
                      component="label"
                      size="small"
                      sx={{ mt: 1 }}
                    >
                      {formData.headerMediaFile ? "Change File" : `Upload ${formData.headerMediaType.charAt(0).toUpperCase() + formData.headerMediaType.slice(1)}`}
                      <input
                        type="file"
                        accept={
                          formData.headerMediaType === "image"
                            ? ".jpg,.jpeg,.png"
                            : formData.headerMediaType === "video"
                            ? ".mp4"
                            : ".pdf"
                        }
                        hidden
                        onChange={(e) => {
                          const file = e.target.files[0];
                          setFormData((prev) => ({ ...prev, headerMediaFile: file }));
                        }}
                      />
                    </Button>
                    {formData.headerMediaFile && (
                      <Box sx={{ mt: 1 }}>
                        {formData.headerMediaType === "image" ? (
                          <img
                            src={URL.createObjectURL(formData.headerMediaFile)}
                            alt="preview"
                            style={{ maxWidth: 120, maxHeight: 80, borderRadius: 4, border: '1px solid #eee' }}
                          />
                        ) : (
                          <Typography variant="body2">
                            {formData.headerMediaFile.name}
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Box>

                  <Alert severity="info" sx={{ fontSize: "0.875rem" }}>
                    <Typography variant="body2">
                      Accepted formats: JPG/PNG (for image), MP4 (for video),
                      PDF (for documents)
                    </Typography>
                  </Alert>
                </Box>
              )}
            </Box>
          )}

          {/* Section 3: Body */}
          {renderSection(
            "Body",
            "To add a custom variable, please include it in curly brackets like {{1}} or {{name}}.",
            <>
              <TextField
                label="Body Content"
                value={formData.body}
                onChange={(e) => handleInputChange("body", e.target.value)}
                error={!!errors.body}
                helperText={errors.body || "Enter your message body content"}
                fullWidth
                multiline
                rows={6}
                size="small"
                placeholder="Enter your message body content. Use {{1}}, {{name}} for variables."
              />
              {/* Dynamic variable sample fields */}
                  {bodyVariables.length > 0 && (
                <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                    Sample Text for Variables:
                  </Typography>
                  {bodyVariables.map((v) => (
                    <TextField
                      key={v}
                      label={`Sample for {{${v}}}`}
                      value={bodyVariableSamples[v] || ""}
                      onChange={(e) =>
                        setBodyVariableSamples((prev) => ({ ...prev, [v]: e.target.value }))
                      }
                      error={!!errors[`variable_body_${v}`]}
                      helperText={errors[`variable_body_${v}`] || `Enter sample text for '{{${v}}}'`}
                      fullWidth
                      size="small"
                    />
                  ))}
                </Box>
              )}
            </>
          )}

          {/* Section 4: Footer */}
          {renderSection(
            "Footer",
            "Variables are not allowed in footer.",
            <TextField
              label="Footer Text"
              value={formData.footer}
              onChange={(e) => handleInputChange("footer", e.target.value)}
              error={!!errors.footer}
              helperText={errors.footer}
              fullWidth
              size="small"
              placeholder="Enter footer text (optional)"
            />
          )}

          {/* Section 5: Call-to-Action Buttons */}
          {renderSection(
            "Call-to-Action Button(s)",
            formData.category === "One-time Password"
              ? "Add a label for the Copy Code button."
              : "Add Phone Number or Website buttons. You can add multiple buttons.",
            <>
              {formData.category === "One-time Password" ? (
                <TextField
                  label="Copy Code Label"
                  value={copyCodeLabel}
                  onChange={(e) => setCopyCodeLabel(e.target.value)}
                  error={!!errors.copyCodeLabel}
                  helperText={errors.copyCodeLabel || "Label for the Copy Code button."}
                  fullWidth
                  size="small"
                  placeholder="e.g. Copy Code"
                />
              ) : (
                <>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                      Add Button(s):
                    </Typography>
                    <Box sx={{ display: "flex", gap: 2 }}>
                      {ctaTypes.map((type) => (
                        <Button
                          key={type.value}
                          variant="outlined"
                          size="small"
                          onClick={() => {
                            setCtaButtons((prev) => [
                              ...prev,
                              { type: type.value, label: "", value: "" },
                            ]);
                          }}
                          disabled={ctaButtons.some((btn) => btn.type === type.value)}
                        >
                          {type.label}
                        </Button>
                      ))}
                    </Box>
                  </Box>
                  {ctaButtons.length > 0 && (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      {ctaButtons.map((btn, idx) => (
                        <Box key={idx} sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                          <TextField
                            label={`Button Label (${btn.type === "phone" ? "Phone" : "Website"})`}
                            value={btn.label}
                            onChange={(e) => {
                              const updated = [...ctaButtons];
                              updated[idx].label = e.target.value;
                              setCtaButtons(updated);
                            }}
                            error={!!errors[`cta_label_${idx}`]}
                            helperText={errors[`cta_label_${idx}`] || "Enter button label"}
                            size="small"
                          />
                          <TextField
                            label={btn.type === "phone" ? "Phone Number" : "Website URL"}
                            value={btn.value}
                            onChange={(e) => {
                              const updated = [...ctaButtons];
                              updated[idx].value = e.target.value;
                              setCtaButtons(updated);
                            }}
                            error={!!errors[`cta_value_${idx}`]}
                            helperText={
                              errors[`cta_value_${idx}`] ||
                              (btn.type === "phone"
                                ? "Enter phone number (e.g. +1234567890)"
                                : "Enter website URL (e.g. https://example.com)")
                            }
                            size="small"
                          />
                          <IconButton
                            aria-label="Remove Button"
                            size="small"
                            onClick={() => {
                              setCtaButtons((prev) => prev.filter((_, i) => i !== idx));
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ))}
                    </Box>
                  )}
                </>
              )}
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, borderTop: "1px solid #e0e0e0", gap: 1 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{
            borderRadius: 1.5,
            textTransform: "none",
            px: 3,
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          startIcon={<SaveIcon />}
          sx={{
            borderRadius: 1.5,
            textTransform: "none",
            px: 3,
            background: "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
            "&:hover": {
              background: "linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)",
            },
          }}
        >
          Save Template
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateTemplateDialog;
